import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Loading from 'components/Loading/index';
import api from 'api';
import Util from 'utility';
import { __ } from 'locale';

import {
    openModal,
    closeModal,
    loadDayPlanCallback,
    setPlannerViewMode,
    resetRecipe,
    setProfileData,
    setLectureTaskData,
    setLectureTaskCompleteSaved,
    addMeal,
    addTraining,
    setAppModeData,
} from '../../actions/index';

import DialogAssignmentInfo from './DialogAssignmentInfo';
import DialogProfileInfo from './DialogProfileInfo';
import DialogSaveLectureTask from './DialogSaveLectureTask';
import DialogLoadLectureTask from './DialogLoadLectureTask';
import DialogLectureTaskSampleText from './DialogLectureTaskSampleText';

import LectureTaskDayPlan from './LectureTask/LectureTaskDayPlan';
import LectureTaskPlan from './LectureTask/LectureTaskPlan';
import LectureTaskAssistantDDD from './LectureTask/LectureTaskAssistantDDD';
import LectureTaskAssistantDDDLower from './LectureTask/LectureTaskAssistantDDDLower';
import DialogSeminarWorkLongtermEvaluation from './LectureTask/DialogSeminarWorkLongtermEvaluation';

const currentDate = new Date('2000-01-01');

function Plan() {
    const data = useSelector(state => state);
    const dispatch = useDispatch();

    const [loading, setLoading] = useState(true);
    const [lectureInfo, setLectureInfo] = useState(false);
    const [toggleViewModePanelOpened, setToggleViewModePanelOpened] = useState(false);

    useEffect(() => {
        Util.setPageTitle('Úkol k lekci');

        const lectureId = data.appModeData.lectureId;
        let url = 'lecturetask/info/' + lectureId;
        if (data.appSubMode === 'LECTURETASK_SAMPLE') url += '?lectureTaskSample=1';

        api(url).then(result => {
            const info = result.data;
            if (info.profile !== false) {
                dispatch(setProfileData({ id: info.profile.id, data: info.profile }));
                dispatch(setAppModeData({ lectureInfoData: info }));
                dispatch(setAppModeData({ profileId: info.profile.id }));
                dispatch(setAppModeData({ undesirableFoods: info.undesirable_foods }));
                dispatch(setAppModeData({ watchAttributes: info.watch_attributes }));
                dispatch(setAppModeData({ watchAlergens: info.watch_alergens }));
            }
            if (info.profile_override !== false) {
                dispatch(setAppModeData({ profileOverride: info.profile_override }));
            }
            setLectureInfo(info);
            setLoading(false);
            dispatch(setLectureTaskData(info));

            api('load/dayplan?date=2000-01-01&clientId=' + data.profiClientId).then(result => {
                const planData = result.data;
                planData.callback = () => {
                    Util.doLoadDayEvaluation();
                    if (typeof planData.empty === 'number') {
                        initLectureTask(info.lecture_data);
                    }
                };
                dispatch(loadDayPlanCallback(planData));
            });
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const initLectureTask = (lectureData) => {
        dispatch(resetRecipe());

        if (Array.isArray(lectureData.meal_types)) {
            lectureData.meal_types.forEach(m => {
                if (m.type * 1 <= 0) return;
                const t = m.time.split(':');
                dispatch(addMeal({
                    mealId: m.type * 1,
                    mealLength: 0,
                    startHour: t[0] * 1,
                    startMinute: t[1] * 1,
                    profiles: [Util.getActiveProfileId()]
                }));
            });
        }

        if (Array.isArray(lectureData.activity_types)) {
            lectureData.activity_types.forEach(a => {
                if (a.time === "" || a.length === "") return;
                const t = a.time.split(':');
                dispatch(addTraining({
                    start: t[0] + ':' + t[1],
                    length: a.length,
                    description: a.title,
                    profiles: [Util.getActiveProfileId()]
                }));
            });
        }
    };

    const reloadTask = () => {
        api('load/dayplan?date=2000-01-01').then(result => {
            const planData = result.data;
            planData.callback = () => {
                Util.doLoadDayEvaluation();
            };
            dispatch(loadDayPlanCallback(planData));

            const lectureData = data.appModeData.lectureInfoData.lecture_data;
            if (Array.isArray(lectureData.activity_types)) {
                lectureData.activity_types.forEach(a => {
                    if (a.time === "" || a.length === "") return;
                    const trainingExists = Object.keys(data.trainings).some(t => data.trainings[t].start === a.time);
                    if (!trainingExists) {
                        const t = a.time.split(':');
                        dispatch(addTraining({
                            start: t[0] + ':' + t[1],
                            length: a.length,
                            description: a.title,
                            profiles: [Util.getActiveProfileId()]
                        }));
                    }
                });
            }
        });
    };

    const toggleViewModePanel = () => setToggleViewModePanelOpened(v => !v);

    const togglePlannerViewMode = (mode) => {
        setToggleViewModePanelOpened(false);
        if (data.plannerViewMode !== mode) {
            dispatch(setPlannerViewMode(mode));
        }
    };

    const saveDayPlan = () => {
        const saveData = JSON.stringify({
            titles: JSON.stringify(data.titles),
            meals: JSON.stringify(data.meals),
            foods: JSON.stringify(data.foods),
            groups: JSON.stringify(data.foodGroups),
            modifications: JSON.stringify(data.modifications),
            activities: JSON.stringify(data.activities),
            trainings: JSON.stringify(data.trainings),
            dayTimestamp: Math.floor(currentDate.getTime() / 1000),
            energyOutputSettings: JSON.stringify(data.energyOutputSettings),
        });
        api('save/dayplan', { post: true, data: saveData });
    };

    const openSaveLectureTaskPanel = (afterClose) => {
        dispatch(closeModal());
        const modalConfig = {
            title: __('Uložit'),
            class: 'dialog-flexible',
            content: <DialogSaveLectureTask defaultTitle={lectureInfo.title} afterClose={afterClose} />,
        };
        if (typeof afterClose === 'function') {
            modalConfig.onClose = afterClose;
        }
        dispatch(openModal(modalConfig));
    };

    const openLoadLectureTaskPanel = () => {
        dispatch(closeModal());
        dispatch(openModal({
            title: __('Načíst úkol'),
            class: 'dialog-big',
            content: <DialogLoadLectureTask onLoaded={onLectureTaskLoaded} />
        }));
    };

    const openAssignmentInfo = () => {
        dispatch(closeModal());
        dispatch(openModal({
            title: __('Zadání úkolu'),
            class: 'dialog-flexible',
            content: <DialogAssignmentInfo assignmentText={lectureInfo.lecture_data.intro_text_new} />
        }));
    };

    const openProfileInfo = () => {
        dispatch(closeModal());
        dispatch(openModal({
            title: __('Informace o profilu'),
            class: 'dialog-flexible',
            content: <DialogProfileInfo />
        }));
    };

    const openLectureTaskSampleText = () => {
        const lectureInfoData = data.appModeData?.lectureInfoData;
        const taskTermGen = Util.getLectureTaskDishTerm(lectureInfoData, 'gen');
        const sampleCommentTitle = Util.getLang() === 'sk'
            ? `Tajomstvo ${taskTermGen}`
            : `Tajemství ${taskTermGen}`;

        dispatch(closeModal());
        dispatch(openModal({
            title: sampleCommentTitle,
            class: 'dialog-flexible',
            content: <DialogLectureTaskSampleText text={lectureInfo.lecture_data.demo_lecture_text} />
        }));
    };

    const openSeminarWorkLongtermEvaluationPanel = () => {
        dispatch(closeModal());
        dispatch(openModal({
            title: __('Dlouhodobé vyhodnocení'),
            class: 'dialog-big',
            content: <DialogSeminarWorkLongtermEvaluation />
        }));
    };

    const onLectureTaskLoaded = (isComplete) => {
        if (!isComplete) {
            dispatch(setLectureTaskCompleteSaved(false));
        }
        dispatch(closeModal());
        reloadTask();
    };

    const lectureTaskAssistantDDD = (e, attr, lower) => {
        e.preventDefault();
        dispatch(closeModal());
        if (typeof lower === 'undefined') {
            dispatch(openModal({
                title: "Potraviny vhodné k doplnění: " + data.globals.attributes[attr],
                closeButton: __('Zavřít'),
                content: (
                    <LectureTaskAssistantDDD attr={attr} onFoodAdded={() => { dispatch(closeModal()); reloadTask(); }} />
                ),
            }));
        } else {
            dispatch(openModal({
                title: "Potraviny s nejvyšším obsahem: " + data.globals.attributes[attr],
                closeButton: __('Zavřít'),
                content: (
                    <LectureTaskAssistantDDDLower attr={attr} onFoodAdded={() => { dispatch(closeModal()); reloadTask(); }} />
                ),
            }));
        }
    };

    if (loading) return <Loading />;

    let viewTypeSwitchText = __('Hodinový');
    if (data.plannerViewMode === 1) viewTypeSwitchText = __('Kompaktní');

    const optionLinkClass = 'block px-4 py-[0.65em] hover:bg-[#efefef] cursor-pointer';
    const toggleViewPanel = toggleViewModePanelOpened ? (
        <div className="absolute z-[10000] left-0 top-[calc(100%+1px)] w-full border border-[#ddd] rounded bg-white">
            <a onClick={() => togglePlannerViewMode(0)} className={optionLinkClass}>{__('Hodinový')}</a>
            <a onClick={() => togglePlannerViewMode(1)} className={optionLinkClass}>{__('Kompaktní')}</a>
        </div>
    ) : false;

    const viewTypeSwitch2 = (
        <div className="py-4 relative">
            <label htmlFor="cb-planner-switch" onClick={toggleViewModePanel} className="whitespace-nowrap font-normal bg-[#F7F7F7] text-[#444] border border-[#ddd] cursor-pointer px-3 py-2 rounded text-center">{__('Přehled')}: <b>{viewTypeSwitchText}</b> <i className="fa fa-angle-down"></i></label>
            {toggleViewPanel}
        </div>
    );

    const lectureTaskPlan = (
        <LectureTaskDayPlan
            date={currentDate}
            onUpdateEvaluation={() => Util.doLoadDayEvaluation()}
            onChange={saveDayPlan}
        />
    );

    return (
        <LectureTaskPlan
            lectureInfo={lectureInfo}
            viewTypeSwitch2={viewTypeSwitch2}
            plan={lectureTaskPlan}
            openAssignmentInfo={openAssignmentInfo}
            openProfileInfo={openProfileInfo}
            openSaveLectureTaskPanel={openSaveLectureTaskPanel}
            openLoadLectureTaskPanel={openLoadLectureTaskPanel}
            openLectureTaskSampleText={openLectureTaskSampleText}
            openSeminarWorkLongtermEvaluationPanel={openSeminarWorkLongtermEvaluationPanel}
        />
    );
}

export default Plan;
